/**
 * A very small observable state holder. set() merges a patch and tells
 * subscribers which keys changed.
 */
export function createState<T extends object>(initial: T) {
  let state: T = { ...initial };
  const listeners = new Set<(state: T, changed: (keyof T & string)[]) => void>();

  return {
    get: (): T => state,
    set(patch: Partial<T>): void {
      const changed = (Object.keys(patch) as (keyof T & string)[]).filter(
        (k) => !Object.is(state[k], patch[k]),
      );
      if (changed.length === 0) return;
      state = { ...state, ...patch };
      for (const fn of [...listeners]) fn(state, changed);
    },
    subscribe(fn: (state: T, changed: (keyof T & string)[]) => void): () => boolean {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}

export type State<T extends object> = ReturnType<typeof createState<T>>;
